import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, api, decodeJwt } from '../context/AuthContext';
import { useSocket } from '../hooks/useSocket';

function tokenLifetimePercent(token) {
  if (!token) return 0;
  const payload = decodeJwt(token);
  if (!payload?.exp || !payload?.iat) return 80;
  const total   = payload.exp - payload.iat;
  const elapsed = Math.floor(Date.now() / 1000) - payload.iat;
  const pct     = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
  return 100 - pct;
}

export default function DocumentEditor() {
  const { id }              = useParams();
  const { user, accessToken, loading: authLoading } = useAuth();
  const navigate            = useNavigate();
  const { socketRef, connected } = useSocket();

  const [doc,       setDoc]       = useState(null);
  const [content,   setContent]   = useState('');
  const [userRole,  setUserRole]  = useState('viewer');
  const [collabs,   setCollabs]   = useState([]);
  const [peers,     setPeers]     = useState([]);
  const [saving,    setSaving]    = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [loadErr,   setLoadErr]   = useState(false);
  const [conflict,  setConflict]  = useState(false);
  const [addEmail,  setAddEmail]  = useState('');
  const [addRole,   setAddRole]   = useState('Viewer');
  const [addErr,    setAddErr]    = useState('');
  const [addOk,     setAddOk]     = useState(false);
  const saveTimeout = useRef(null);
  const versionRef  = useRef(1); // tracked in a ref so the debounced save always reads the latest known version

  const canEdit = ['admin', 'editor'].includes(userRole);
  const isAdmin = userRole === 'admin';

  const loadDoc = useCallback(() => {
    return api.get(`/documents/${id}`)
      .then(({ data }) => {
        const d = data.document;
        setDoc(d);
        setContent(d.content || '');
        versionRef.current = d.version ?? 1;

        const rawRole = data.userRole ?? data.role ?? d.userRole ?? d.role ?? 'viewer';
        setUserRole(rawRole.toLowerCase());

        setCollabs(d.collaborators || []);
        const txt = d.content || '';
        setWordCount(txt.trim() ? txt.trim().split(/\s+/).length : 0);
        setLoadErr(false);
        setConflict(false);
      });
  }, [id]);

  useEffect(() => {
    if (authLoading) return;
    loadDoc().catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('[DocumentEditor] Failed to load document:', err?.response?.status, err?.response?.data);
      }
      setLoadErr(true);
    });
  }, [id, authLoading, loadDoc]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !doc || !connected) return;

    socket.emit('join-document', id);

    const onUpdate = ({ delta }) => {
      setContent(delta);
      setWordCount(delta.trim() ? delta.trim().split(/\s+/).length : 0);
    };
    const onJoined = ({ userId }) => setPeers(p => [...new Set([...p, userId])]);
    const onLeft   = ({ userId }) => setPeers(p => p.filter(u => u !== userId));

    socket.on('document-update', onUpdate);
    socket.on('user-joined',     onJoined);
    socket.on('user-left',       onLeft);

    return () => {
      socket.emit('leave-document', id);
      socket.off('document-update', onUpdate);
      socket.off('user-joined',     onJoined);
      socket.off('user-left',       onLeft);
    };
  }, [id, doc, connected, socketRef]);

  // ── Auto-save ────────────────────────────────────────────────────────────
  // FIX: previously saved with no version check at all, so two people typing
  // near-simultaneously silently overwrote each other with no warning. Now
  // sends expectedVersion; on a 409 from the server we stop clobbering the
  // in-progress edit and surface a conflict banner instead of losing work.
  const saveContent = useCallback(async (text) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/documents/${id}`, {
        content: text,
        expectedVersion: versionRef.current,
      });
      versionRef.current = data.document.version;
      setDoc(data.document);
      setLastSaved(new Date());
    } catch (e) {
      if (e.response?.status === 409) {
        setConflict(true);
      } else {
        console.error('Save failed:', e);
      }
    } finally {
      setSaving(false);
    }
  }, [id]);

  const handleChange = (e) => {
    const text = e.target.value;
    setContent(text);
    setWordCount(text.trim() ? text.trim().split(/\s+/).length : 0);
    socketRef.current?.emit('document-change', { docId: id, delta: text, version: versionRef.current });
    clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => saveContent(text), 1500);
  };

  const reloadAfterConflict = async () => {
    try {
      await loadDoc();
    } catch (e) {
      console.error(e);
    }
  };

  const handleTitleBlur = async (e) => {
    const newTitle = e.target.value.trim();
    if (canEdit && newTitle && newTitle !== doc?.title) {
      try {
        const { data } = await api.put(`/documents/${id}`, { title: newTitle, expectedVersion: versionRef.current });
        versionRef.current = data.document.version;
        setDoc(prev => ({ ...prev, title: newTitle, version: data.document.version }));
      } catch (err) {
        if (err.response?.status === 409) setConflict(true);
        else console.error('Title save failed:', err);
      }
    }
  };

  const handleAddCollab = async (e) => {
    e.preventDefault();
    setAddErr(''); setAddOk(false);
    try {
      const { data: teamData } = await api.get('/team');
      const found = (teamData.users || []).find(
        u => u.email.toLowerCase() === addEmail.toLowerCase().trim()
      );
      if (!found)                  { setAddErr('No team member found with that email.'); return; }
      if (found._id === user?._id) { setAddErr('You are already the owner.'); return; }

      await api.post(`/documents/${id}/collaborators`, { userId: found._id, role: addRole });

      const { data } = await api.get(`/documents/${id}`);
      setCollabs(data.document.collaborators || []);
      setAddEmail(''); setAddOk(true);
      setTimeout(() => setAddOk(false), 2500);
    } catch (err) {
      setAddErr(err.response?.data?.message || 'Failed to add collaborator.');
    }
  };

  const avatarColor = (id = '') => {
    const colors = ['#4f46e5','#10b981','#f59e0b','#ec4899','#06b6d4','#8b5cf6'];
    let n = 0;
    for (let i = 0; i < id.length; i++) n += id.charCodeAt(i);
    return colors[n % colors.length];
  };

  const initials = (name = '') =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const roleClass = r =>
    ({ admin: 'role-admin', editor: 'role-editor', viewer: 'role-viewer' }[(r || '').toLowerCase()] || 'role-viewer');

  const accessTokenPct  = tokenLifetimePercent(accessToken);
  const refreshTokenPct = 95;

  if (authLoading) return (
    <div className="loading">
      <div className="loading-spinner" />
      <span>Loading…</span>
    </div>
  );

  if (loadErr) return (
    <div className="loading">
      <p style={{ color: 'var(--danger)', marginBottom: '0.75rem' }}>Could not load document.</p>
      <button className="btn-ghost" onClick={() => navigate('/dashboard')}>← Back to dashboard</button>
    </div>
  );

  if (!doc) return (
    <div className="loading">
      <div className="loading-spinner" />
      <span>Loading document…</span>
    </div>
  );

  return (
    <div className="editor-page">

      {conflict && (
        <div style={{ background: 'var(--warn, #fef3c7)', color: 'var(--warn-text, #92400e)', padding: '8px 16px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>This document was changed elsewhere — your last save didn't go through.</span>
          <button className="btn-ghost" style={{ fontSize: '0.78rem' }} onClick={reloadAfterConflict}>Reload latest version</button>
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="editor-toolbar">
        <div className="editor-toolbar-left">
          <button className="btn-ghost" onClick={() => navigate('/dashboard')}>← Back</button>
          <input
            className="doc-title-input"
            defaultValue={doc.title}
            disabled={!canEdit}
            onBlur={handleTitleBlur}
          />
        </div>

        <div className="editor-status">
          {peers.length > 0 && (
            <div className="peers-pill">
              <span className="peers-pulse" />
              {peers.length} online
            </div>
          )}
          <span className={`save-status ${saving ? 'saving' : lastSaved ? 'saved' : ''}`}>
            {saving ? (
              <>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                Saving…
              </>
            ) : lastSaved ? (
              <>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </>
            ) : null}
          </span>
          <span className={`role-badge ${roleClass(userRole)}`}>{userRole}</span>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="editor-body">

        {/* ── Left sidebar ── */}
        <div className="editor-sidebar">

          <div>
            <h4>Collaborators</h4>

            <div className="collab-item">
              <div className="collab-avatar" style={{ background: avatarColor(doc.owner?._id || '') }}>
                {initials(doc.owner?.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="collab-name">{doc.owner?.name || 'Owner'}</div>
                <div className="collab-status">
                  {doc.owner?._id === user?._id ? 'you · owner' : 'owner'}
                </div>
              </div>
              <div className="collab-dot" style={{ background: peers.includes(doc.owner?._id) || doc.owner?._id === user?._id ? '#10b981' : '#d4d4d4' }} />
            </div>

            {collabs.map((c, i) => {
              const cId    = c.user?._id || String(i);
              const cName  = c.user?.name || 'Collaborator';
              const isOnline = peers.includes(cId) || cId === user?._id;
              return (
                <div className="collab-item" key={cId}>
                  <div className="collab-avatar" style={{ background: avatarColor(cId) }}>
                    {initials(cName)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="collab-name">{cName}{cId === user?._id ? ' (you)' : ''}</div>
                    <div className="collab-status">
                      <span className={`role-badge ${roleClass(c.role)}`} style={{ fontSize: '0.62rem', padding: '1px 5px' }}>{c.role}</span>
                      {isOnline && <span style={{ color: 'var(--success)', marginLeft: '4px' }}>· online</span>}
                    </div>
                  </div>
                  <div className="collab-dot" style={{ background: isOnline ? '#10b981' : '#d4d4d4' }} />
                </div>
              );
            })}
          </div>

          {isAdmin && (
            <div>
              <h4>Add collaborator</h4>
              <form onSubmit={handleAddCollab} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <input
                  type="email"
                  placeholder="Email address"
                  value={addEmail}
                  onChange={e => setAddEmail(e.target.value)}
                  required
                  style={{ fontSize: '0.75rem', padding: '4px 7px', borderRadius: 'var(--radius)', border: '1px solid var(--border-hover)', background: 'var(--surface)', color: 'var(--text)', outline: 'none', fontFamily: 'inherit' }}
                />
                <select
                  value={addRole}
                  onChange={e => setAddRole(e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '4px 7px', borderRadius: 'var(--radius)', border: '1px solid var(--border-hover)', background: 'var(--surface)', color: 'var(--text)', outline: 'none', fontFamily: 'inherit' }}
                >
                  <option value="Editor">Editor</option>
                  <option value="Viewer">Viewer</option>
                  <option value="Admin">Admin</option>
                </select>
                <button type="submit" className="btn-primary" style={{ fontSize: '0.75rem', padding: '4px 8px', justifyContent: 'center' }}>
                  Add
                </button>
                {addErr && <div style={{ fontSize: '0.7rem', color: 'var(--danger)' }}>{addErr}</div>}
                {addOk  && <div style={{ fontSize: '0.7rem', color: 'var(--success-text)' }}>Added successfully.</div>}
              </form>
            </div>
          )}

          <div>
            <h4>Your access</h4>
            <span className={`role-badge ${roleClass(userRole)}`}>
              {userRole === 'admin'  ? 'Admin — full edit'
               : userRole === 'editor' ? 'Editor — can edit'
               : 'Viewer — read only'}
            </span>
          </div>

          <div>
            <h4>Token status</h4>
            <div className="token-bar-wrap">
              <div className="token-bar-label">
                <span>Access token</span>
                <span style={{ color: accessTokenPct > 20 ? 'var(--success-text)' : 'var(--warn-text)' }}>
                  {accessTokenPct}% left
                </span>
              </div>
              <div className="token-bar">
                <div className="token-bar-fill" style={{ width: `${accessTokenPct}%` }} />
              </div>
              <div className="token-bar-label">
                <span>Refresh token</span>
                <span style={{ color: 'var(--text3)' }}>httpOnly cookie</span>
              </div>
              <div className="token-bar">
                <div className="token-bar-fill" style={{ width: `${refreshTokenPct}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Editor area ── */}
        <div className="editor-area-wrap">
          <textarea
            className="editor-area"
            value={content}
            onChange={handleChange}
            disabled={!canEdit}
            placeholder={canEdit
              ? 'Start writing… changes save automatically.'
              : 'You have view-only access to this document.'}
            spellCheck
          />
          <div className="editor-footer">
            <span>{doc.title} · v{doc.version ?? 1}</span>
            <span>{wordCount} {wordCount === 1 ? 'word' : 'words'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
